import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CalculatorsComponent } from './calculators/calculators.component';
import { ErrorsSearchComponent } from './error-reporting/errors-search/errors-search.component';
import { AuthGuard } from './guards/auth-guard';
import { UserGuard } from './guards/user-guard';
import { MenuSettingComponent } from './settings/menu-setting/menu-setting.component';
import { SelectivePreloadingStrategy } from './selective-preloading.strategy';

const routes: Routes = [
    {
        path: '',
        loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
        data: {
            colouredBackground: false,
            offset: true,
            showMenu: false,
            showHeader: false,
            showFooter: false,
            disableAuthTimeout: true,
        },
    },
    {
        path: 'password',
        loadChildren: () => import('./auth-reset/auth-reset.module').then((m) => m.AuthResetModule),
        data: {
            colouredBackground: false,
            offset: true,
            showMenu: false,
            showHeader: false,
            showFooter: false,
            disableAuthTimeout: true,
        },
    },

    {
        path: 'accounts',
        loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
        canLoad: [AuthGuard],
        data: {
            preload: true,
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'assistant',
        loadChildren: () => import('./assistant/ai-assistant.module').then((m) => m.AiAssistantModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'account',
        loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'calculator',
        component: CalculatorsComponent,
    },
    {
        path: 'jobs',
        loadChildren: () => import('./job/job.module').then((m) => m.JobModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'chat',
        loadChildren: () => import('./chat/chat.module').then((m) => m.ChatModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
        data: {
            preload: true,
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'chat/:chatId',
        loadChildren: () => import('./chat/chat.module').then((m) => m.ChatModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
    },
    {
        path: 'chat/:chatId/:messageId',
        loadChildren: () => import('./chat/chat.module').then((m) => m.ChatModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
    },
    {
        path: 'stock',
        loadChildren: () => import('./stock/stock.module').then((m) => m.StockModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'products',
        loadChildren: () => import('./products/products.module').then((m) => m.ProductsModule),
        canLoad: [AuthGuard],
        data: {
            preload: true,
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-types',
        loadChildren: () => import('./product-types/product-types.module').then((m) => m.ProductTypesModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-variables',
        loadChildren: () =>
            import('./product-variables/product-variables.module').then((m) => m.ProductVariablesModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-variable-options/:productVariable',
        loadChildren: () =>
            import('./product-variable-options/product-variable-options.module').then(
                (m) => m.ProductVariableOptionsModule
            ),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-images-bespoke',
        loadChildren: () =>
            import('./product-images-bespoke/product-images-bespoke.module').then((m) => m.ProductImagesBespokeModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-pricing',
        loadChildren: () => import('./product-pricing/product-pricing.module').then((m) => m.ProductPricingModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-ranges',
        loadChildren: () => import('./product-range/product-range.module').then((m) => m.ProductRangeModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'product-groups',
        loadChildren: () => import('./product-group/product-group.module').then((m) => m.ProductGroupModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'actions',
        loadChildren: () => import('./actions/actions.module').then((m) => m.ActionsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'actions/:id',
        pathMatch: 'full',
        loadChildren: () => import('./actions/actions.module').then((m) => m.ActionsModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
    },
    {
        path: 'orders',
        loadChildren: () => import('./orders/orders.module').then((m) => m.OrdersModule),
        canLoad: [AuthGuard],
        data: {
            preload: true,
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'order',
        loadChildren: () => import('./order-create/order-create.module').then((m) => m.OrderCreateModule),
        canLoad: [AuthGuard],
        data: {
            preload: true,
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'payments',
        loadChildren: () => import('./payments/payments.module').then((m) => m.PaymentsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },

    {
        path: 'postcode',
        loadChildren: () => import('./postcode/postcode.module').then((m) => m.PostcodeModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'staff',
        loadChildren: () => import('./staff/staff.module').then((m) => m.StaffModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'terms',
        loadChildren: () => import('./terms/terms.module').then((m) => m.TermsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },

    {
        path: 'rewards',
        loadChildren: () => import('./reward/reward.module').then((m) => m.RewardModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'reporting',
        loadChildren: () => import('./reporting/reporting.module').then((m) => m.ReportingModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'case-studies',
        loadChildren: () => import('./case-study/case-study.module').then((m) => m.CaseStudyModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'advice',
        loadChildren: () => import('./advice/advice.module').then((m) => m.AdviceModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'cms',
        loadChildren: () => import('./cms/cms.module').then((m) => m.CmsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },

    {
        path: 'cms-new',
        loadChildren: () => import('./upero-cms/upero-cms.module').then((m) => m.UperoCmsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'blog',
        loadChildren: () => import('./blog/blog.module').then((m) => m.BlogModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'questionnaire',
        loadChildren: () => import('./questionnaire/questionnaire.module').then((m) => m.QuestionnaireModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },

    {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'bespoke-requests',
        loadChildren: () => import('./bespoke-requests/bespoke-requests.module').then((m) => m.BespokeRequestsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },

    {
        path: 'requests',
        loadChildren: () => import('./requests/requests.module').then((m) => m.RequestsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'notifications',
        loadChildren: () => import('./notifications/notifications.module').then((m) => m.NotificationsModule),
        canLoad: [AuthGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'systemerrors',
        component: ErrorsSearchComponent,
        canActivate: [UserGuard],
        data: {
            menuGroup: ['systemerrors'],
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'menusetting',
        component: MenuSettingComponent,
        canActivate: [UserGuard],
        data: {},
    },
    {
        path: 'leads',
        loadChildren: () => import('./leads/leads.module').then((m) => m.LeadsModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
        data: {
            colouredBackground: true,
            offset: true,
            showMenu: true,
            showHeader: true,
            showFooter: true,
            disableAuthTimeout: false,
        },
    },
    {
        path: 'elements-catalog',
        loadChildren: () => import('./elements-catalog/elements-catalog.module').then((m) => m.ElementsCatalogModule),
        canLoad: [AuthGuard],
        canActivate: [UserGuard],
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            onSameUrlNavigation: 'reload',
            relativeLinkResolution: 'legacy' /*, enableTracing: true*/,
            // PERF: preload hot routes (flagged data:{preload:true}) after idle so daily
            // screens navigate instantly without growing the initial bundle.
            preloadingStrategy: SelectivePreloadingStrategy,
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule { }
